<template>
  <div class="activate-view">
    <div v-if="loadingInvitation" class="activate-card activate-card--center">
      <span class="mdi mdi-loading activate-card__spinner"></span>
      <span class="activate-card__loading-text">Verificando invitación...</span>
    </div>

    <div v-else-if="invitationError" class="activate-card activate-card--center">
      <span class="mdi mdi-alert-circle-outline activate-card__error-icon"></span>
      <p class="activate-card__title">{{ invitationErrorTitle }}</p>
      <p class="activate-card__subtitle">{{ invitationErrorText }}</p>
      <router-link class="activate-card__link" to="/login">Ir a iniciar sesión</router-link>
    </div>

    <form v-else class="activate-card" @submit.prevent="onSubmit">
      <h1 class="activate-card__title">Activa tu cuenta</h1>
      <p class="activate-card__subtitle">
        {{ invitation.email }} · {{ invitation.parkName }}
      </p>

      <label class="activate-card__label" for="activate-name">Nombre completo</label>
      <input id="activate-name" v-model.trim="name" type="text" class="activate-card__input" required />

      <label class="activate-card__label" for="activate-password">Contraseña</label>
      <input
        id="activate-password"
        v-model="password"
        type="password"
        class="activate-card__input"
        autocomplete="new-password"
        minlength="8"
        required
      />

      <label class="activate-card__label" for="activate-password-confirm">Confirmar contraseña</label>
      <input
        id="activate-password-confirm"
        v-model="passwordConfirm"
        type="password"
        class="activate-card__input"
        autocomplete="new-password"
        minlength="8"
        required
      />

      <p v-if="errorMessage" class="activate-card__error">{{ errorMessage }}</p>

      <button type="submit" class="activate-card__submit" :disabled="submitting">
        <span v-if="submitting" class="mdi mdi-loading activate-card__spinner"></span>
        <span>{{ submitting ? 'Activando...' : 'Activar cuenta' }}</span>
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getInvitationInfo, activateAccount } from '@/services/auth'
import { useAuthStore } from '@/services/authStore'

const route = useRoute()
const router = useRouter()
const { refreshUser } = useAuthStore()

const token = route.query.token || ''

const loadingInvitation = ref(true)
const invitationError = ref('')
const invitation = ref(null)

const name = ref('')
const password = ref('')
const passwordConfirm = ref('')
const submitting = ref(false)
const errorMessage = ref('')

const ERROR_TITLES = {
  INVITATION_NOT_FOUND: 'Invitación no encontrada',
  INVITATION_EXPIRED: 'Invitación expirada',
  ALREADY_ACTIVATED: 'Esta cuenta ya está activa',
}
const ERROR_TEXTS = {
  INVITATION_NOT_FOUND: 'El enlace de invitación no es válido. Pide a un administrador que te invite de nuevo.',
  INVITATION_EXPIRED: 'Este enlace ya expiró. Pide a un administrador que reenvíe la invitación.',
  ALREADY_ACTIVATED: 'Ya puedes iniciar sesión con tu correo y contraseña.',
}

const invitationErrorTitle = computed(() => ERROR_TITLES[invitationError.value] || 'No se pudo abrir la invitación')
const invitationErrorText = computed(() => ERROR_TEXTS[invitationError.value] || 'Intenta de nuevo más tarde.')

onMounted(async () => {
  if (!token) {
    invitationError.value = 'INVITATION_NOT_FOUND'
    loadingInvitation.value = false
    return
  }
  try {
    const info = await getInvitationInfo(token)
    invitation.value = info
    name.value = info.name || ''
  } catch (err) {
    invitationError.value = err.message || 'INVITATION_NOT_FOUND'
  } finally {
    loadingInvitation.value = false
  }
})

async function onSubmit() {
  errorMessage.value = ''
  if (password.value !== passwordConfirm.value) {
    errorMessage.value = 'Las contraseñas no coinciden.'
    return
  }
  if (password.value.length < 8) {
    errorMessage.value = 'La contraseña debe tener al menos 8 caracteres.'
    return
  }

  submitting.value = true
  try {
    await activateAccount({ token, password: password.value, name: name.value })
    await refreshUser()
    router.push('/inicio')
  } catch (err) {
    errorMessage.value = 'No se pudo activar la cuenta. Intenta de nuevo.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.activate-view {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e6eaf3;
  padding: 1rem;
}

.activate-card {
  width: 100%;
  max-width: 380px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(30, 58, 95, 0.12);
  padding: 2rem 1.75rem;
  display: flex;
  flex-direction: column;
}

.activate-card--center {
  align-items: center;
  text-align: center;
  gap: 0.5rem;
}

.activate-card__title {
  font-family: var(--font-display);
  font-size: 1.3rem;
  color: #1e3a5f;
  margin: 0 0 0.25rem;
}

.activate-card__subtitle {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: #64748b;
  margin: 0 0 1.5rem;
}

.activate-card__label {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0.3rem;
}

.activate-card__input {
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: #1e3a5f;
  border: 1px solid var(--border-active);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 1rem;
}
.activate-card__input:focus {
  outline: none;
  border-color: #3b82f6;
}

.activate-card__error {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: #e84545;
  margin: -0.5rem 0 1rem;
}

.activate-card__error-icon {
  font-size: 2rem;
  color: #e84545;
}

.activate-card__link {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: #3b82f6;
  margin-top: 0.5rem;
}

.activate-card__submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #1e3a5f;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.7rem;
  font-family: var(--font-body);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}
.activate-card__submit:hover:not(:disabled) { background: #163049; }
.activate-card__submit:disabled { opacity: 0.7; cursor: default; }

.activate-card__loading-text {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: #64748b;
}

.activate-card__spinner {
  animation: activate-spin 0.8s linear infinite;
  font-size: 1.4rem;
  color: #1e3a5f;
}
@keyframes activate-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>